"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import { type CarouselApi } from "@/components/ui/carousel";

interface Props {
  key: number;
  gallery: {
    images: Array<{
      url: string;
      altText: string;
    }>;
  };
}

export default function Gallery({ gallery, key }: Props) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [currentPage, setCurrentPage] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);

  const updatePagination = useCallback(() => {
    if (!api) return;
    const count = api.scrollSnapList().length;
    setTotalPages(count);
    setCurrentPage(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;

    updatePagination();
    api.on("select", updatePagination);
    api.on("reInit", updatePagination);

    // Wichtig: Wir fügen auch einen Listener für 'resize' hinzu
    window.addEventListener("resize", updatePagination);

    return () => {
      api.off("select", updatePagination);
      api.off("reInit", updatePagination);
      window.removeEventListener("resize", updatePagination);
    };
  }, [api, updatePagination]);

  const CarouselPagination = () => (
    <div className="mx-2 flex flex-wrap justify-center gap-2">
      {Array.from({ length: totalPages }).map((_, index) => (
        <Button
          key={index}
          variant={currentPage === index ? "simplePrimary" : "simple"}
          size="icon"
          className="h-3 w-3 rounded-full border-none bg-[#E5E5E5]"
          onClick={() => api?.scrollTo(index)}
          aria-label={`Seite ${index + 1}`}
        >
          {/* {index + 1} */}
        </Button>
      ))}
    </div>
  );

  const renderThis = (gallery: any) => {
    return !!(gallery?.images?.length > 0);
  };

  if (renderThis(gallery)) {
    const count = gallery.images.length;

    let countClass = ""; // Default-Wert
    if (count == 2) {
      countClass = "md:basis-1/2";
    } else if (count == 3) {
      countClass = "sm:basis-1/2 md:basis-1/3";
    } else if (count > 3) {
      countClass = "sm:basis-1/2 md:basis-1/4";
    }

    return (
      <div className="mx-auto mb-10 w-auto">
        <Carousel
          setApi={setApi}
          opts={{
            align: "start",
            loop: true,
            slidesToScroll: "auto",
          }}
        >
          <CarouselContent>
            {gallery.images.map((image, index) => (
              <CarouselItem className={`flex basis-full flex-row justify-center ${countClass}`} key={index}>
                <Image
                  src={image.url}
                  alt={image.altText || ""}
                  width={1000}
                  height={1000}
                  className={`${count > 1 ? "w-auto" : "max-w-xs"}`}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          {count > 1 ? (
            <div className="relative mx-auto mt-8 flex max-w-xs flex-row items-center justify-center">
              <CarouselPrevious aria-label="Vorherige Seite" />
              <CarouselPagination />
              <CarouselNext aria-label="Nächste Seite" />
            </div>
          ) : (
            ""
          )}
        </Carousel>
      </div>
    );
  }
}
